// src/lib/operateur-a-traiter.js — UNE SEULE LISTE DE CHOSES À FAIRE.
//
// ══ LA DEMANDE (Dimitri, 2026-09-15) ═══════════════════════════════════════
//
// « Mes tâches en retard et mes événements du jour sont la même chose de mon
// point de vue — des choses à faire ; les séparer m'oblige à regarder à deux
// endroits. »
//
// Et la règle qui va avec, écrite dans sa maquette au-dessus de `nbUrgents` :
// « un compteur et sa liste doivent toujours dire la même chose ». La pastille
// disait « 3 à traiter » et la liste s'ouvrait VIDE, parce que l'une comptait
// les états et l'autre montrait le fil. D'où cette fonction UNIQUE : la
// pastille affiche sa longueur, la feuille affiche ses lignes. Il n'y a pas
// deux calculs, donc il n'y a pas deux vérités.
//
// ══ DEUX SOURCES, UN SEUL ORDRE ════════════════════════════════════════════
//
// · LES ÉTATS DURABLES — les lignes de l'accueil (`aFaire`), tons « u » et
//   « a ». Elles ne sont pas dans le fil (elles ne « tombent » pas : elles
//   durent), et elles restent à faire. Les tâches en retard en font partie.
// · LES ÉVÉNEMENTS DU JOUR QUI PRESSENT — poids ≥ 50, ni traités ni rangés.
//
// ⚠️ LE FIL SUIT LA CHRONOLOGIE ; CETTE LISTE SUIT L'URGENCE. Ce sont deux
// lectures du même matériau, et c'est voulu : on lit sa journée dans l'ordre
// où elle est arrivée, on traite ses affaires dans l'ordre de ce qu'elles
// coûtent.

import { poidsEvenement, SEUIL_PRESSE } from "./evenements-poids";
import { heureDe } from "./evenements-jour";

/**
 * Le poids d'un état durable. Il n'a pas de phrase à peser : son TON dit déjà
 * ce qu'il vaut (« u » = ce qui bloque, « a » = ce qui attend). Deux valeurs,
 * posées pour qu'un état urgent passe devant un constat chiffré et derrière un
 * voyageur devant une porte — c'est le barème de la maquette.
 */
export const POIDS_ETAT = { u: 60, a: 40 };

/**
 * ══ LE MÊME FAIT, VU DE DEUX CÔTÉS ═════════════════════════════════════════
 *
 * Dimitri, 2026-09-16, dans sa liste : « 3 · MÉNAGE — Ménage du 15/09 sans
 * agent, Sur un Nuage » ET « 16 · À FAIRE — Un ménage sans agent, Sur un
 * Nuage · avant 11h ». Deux lignes, un seul ménage.
 *
 * C'est structurel : l'accueil compose ses ÉTATS depuis `menage_runs`, et
 * l'opérateur annonce les MÊMES runs comme événements. Les deux ont raison
 * chacun de leur côté ; c'est leur réunion qui double.
 *
 * ⚠️ ON DÉDOUBLONNE SUR L'IDENTIFIANT DU FAIT, PAS SUR LE TEXTE. Les deux
 * phrases ne se ressemblent pas (« avant 11h » d'un côté, « personne n'est
 * prévu » de l'autre) — seule la clé dit qu'il s'agit du même run. D'où cette
 * table : un préfixe d'état, les préfixes d'événements qui parlent du même
 * objet, et l'identifiant en dernier segment des deux côtés.
 */
const MEMES_FAITS = [
  // `sans-agent:<run_id>` ↔ `menage_sans_agent:<run_id>`
  { etat: "sans-agent", evenements: ["menage_sans_agent"] },
  // `depart-reporte:<run_id>` ↔ `depart_reporte_menage:<run_id>`
  { etat: "depart-reporte", evenements: ["depart_reporte_menage"] },
  // `incident:<id>` ↔ les deux annonces qui lisent la même table
  { etat: "incident", evenements: ["incident_a_racheter", "casse_signalee"] },
  // `reprise:<controle_id>` ↔ `controle_a_decider:<controle_id>`
  { etat: "reprise", evenements: ["controle_a_decider"] },
  // `signalement:<famille>:<id>` ↔ l'annonce du même signalement
  {
    etat: "signalement",
    evenements: ["signalement_sejour", "etat_des_lieux_recu"],
  },
];

/** L'identifiant du FAIT derrière une clé — le dernier segment. */
function _dernierSegment(cle) {
  const parts = String(cle || "").split(":");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

/**
 * L'empreinte d'une ligne : ce qui permet de reconnaître qu'un état et une
 * annonce parlent du même fait. `null` quand la ligne n'a pas de jumelle
 * possible — et dans ce cas elle ne se dédoublonne avec rien.
 */
function _empreinte(ligne) {
  const cle = String(ligne.cle || "");
  const tete = cle.split(":")[0];
  for (const paire of MEMES_FAITS) {
    if (ligne.etat && tete === paire.etat)
      return `${paire.etat}:${_dernierSegment(cle)}`;
    if (!ligne.etat && paire.evenements.includes(tete))
      return `${paire.etat}:${_dernierSegment(cle)}`;
  }
  return null;
}

/**
 * Fond deux lignes qui désignent le même fait : la plus LOURDE gagne (c'est
 * elle qui dit le mieux ce que ça coûte), et elle hérite de ce qui lui manque
 * pour agir — le geste, le clic, la coche. Perdre le geste de l'état en
 * gardant la phrase de l'événement donnerait une ligne qui ne mène nulle part.
 */
function _fondre(a, b) {
  const [gagne, perd] = a.poids >= b.poids ? [a, b] : [b, a];
  return {
    ...gagne,
    geste: gagne.geste || perd.geste || null,
    ecran: gagne.ecran || perd.ecran || null,
    onClic: gagne.onClic || perd.onClic || null,
    onTraite: gagne.onTraite || perd.onTraite || null,
    tag: gagne.tag || perd.tag || null,
    // La ligne fondue garde le poids du gagnant : elle occupe SA place.
    poids: Math.max(a.poids, b.poids),
  };
}

/**
 * Tout ce qui reste à faire, dans l'ordre de ce que ça coûte d'attendre.
 *
 * @param {{aFaire?:{lignes?:Array}, evenements?:Array, jour?:string}} entree
 *   `evenements` = la vue DU FIL (ni traités ni rangés) ; c'est à l'appelant
 *   de la filtrer, pour que la liste et l'écran parlent du même matériau.
 * @returns {Array<{cle,nature,heure,texte,poids,geste,ecran,libelle,onClic,etat}>}
 */
export function composerATraiter(entree = {}) {
  const jour = entree.jour || null;
  const lignes = (entree.aFaire && entree.aFaire.lignes) || [];
  const etats = lignes
    .filter((l) => l && (l.ton === "u" || l.ton === "a"))
    .map((l) => ({
      cle: l.cle,
      // La nature d'un état : le premier mot de son titre, comme la maquette
      // (« Un ménage sans agent » ⇒ MÉNAGE n'existe pas comme champ ici).
      nature: l.nature || "à faire",
      ton: l.ton,
      heure: "",
      texte: [l.titre, l.sous].filter(Boolean).join(" — "),
      poids: POIDS_ETAT[l.ton] || 0,
      geste: null,
      ecran: null,
      libelle: "Voir",
      onClic: typeof l.onClic === "function" ? l.onClic : null,
      // La coche « Rien à répondre » de la liste compacte : elle suit sa
      // ligne ici plutôt que de disparaître avec l'ancienne vue.
      onTraite: typeof l.onTraite === "function" ? l.onTraite : null,
      tag: l.tag || null,
      etat: true,
    }));

  const evts = (entree.evenements || [])
    .filter(Boolean)
    .map((e) => ({ e, poids: poidsEvenement(e, { jour }) }))
    .filter((x) => x.poids >= SEUIL_PRESSE)
    .map(({ e, poids }) => ({
      cle: e.cle,
      nature: e.nature || "",
      ton: "u",
      heure: heureDe(e),
      texte: e.phrase || "",
      poids,
      geste: e.geste || null,
      ecran: e.ecran || null,
      libelle: e.libelle || "Voir",
      onClic: null,
      onTraite: null,
      tag: null,
      etat: false,
    }));

  // ⚠️ DÉDOUBLONNER AVANT DE TRIER — et la pastille compte CETTE liste : elle
  // ne peut donc pas annoncer six choses à faire quand il y en a trois.
  const parEmpreinte = new Map();
  const toutes = [];
  for (const ligne of [...etats, ...evts]) {
    const empreinte = _empreinte(ligne);
    if (!empreinte) {
      toutes.push(ligne);
      continue;
    }
    const deja = parEmpreinte.get(empreinte);
    if (deja) parEmpreinte.set(empreinte, _fondre(deja, ligne));
    else parEmpreinte.set(empreinte, ligne);
  }
  return [...toutes, ...parEmpreinte.values()].sort(
    (a, b) => b.poids - a.poids || String(a.cle).localeCompare(String(b.cle)),
  );
}

/**
 * Le ton d'un rang dans la liste — le rond numéroté de la maquette.
 * ≥ 130 : chaud (terracotta). ≥ 80 : tiède (ambre). Sinon : gris.
 */
export function tonDuPoids(poids) {
  const p = Number(poids) || 0;
  if (p >= 130) return "chaud";
  if (p >= 80) return "tiede";
  return "froid";
}
